<template>
  <!-- eslint-disable max-len -->
  <div :class="['progress-ring', { 'progress-ring--small': size === 'small' }]">
    <svg
      v-if="showProgressRingIndicator"
      ref="svg"
      :viewBox="`0 0 ${ canvasSizeX } ${ canvasSizeY}`"
      overflow="visible"
    >
      <circle
        class="progress-ring__circle-bottom"
        :r="graphRadius"
        :cx="canvasSizeX / 2"
        :cy="canvasSizeY / 2"
        :stroke-width="strokeWidth"
      />

      <circle
        ref="circle"
        class="progress-ring__circle-top"
        :r="graphRadius"
        :cx="(canvasSizeX / 2) * -1"
        :cy="canvasSizeY / 2"
        :stroke-width="strokeWidth"
        transform="rotate(-90)"
      />

      <g
        v-for="(checkpoint, index) in calculatedCheckpoints"
        :key="`${checkpoint.value}-${index}`"
        class="progress-ring__checkpoint"
        :class="{
          'progress-ring__checkpoint--activated': checkpoint.isActivated
        }"
      >
        <circle
          v-tippy="checkpoint.content"
          :r="checkpointRadius"
          :cx="checkpoint.x"
          :cy="checkpoint.y"
        />

        <svg
          class="progress-ring__checkpoint-icon-activated"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          :width="checkpointIconSize"
          :height="checkpointIconSize"
          :x="checkpoint.x - (checkpointIconSize / 2)"
          :y="checkpoint.y - (checkpointIconSize / 2)"
        >
          <path
            fill="currentColor"
            d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z"
          />
        </svg>

        <svg
          class="progress-ring__checkpoint-icon-deactivated"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 576 512"
          :width="checkpointIconSize"
          :height="checkpointIconSize"
          :x="checkpoint.x - (checkpointIconSize / 2)"
          :y="checkpoint.y - (checkpointIconSize / 2)"
        >
          <path
            fill="currentColor"
            d="M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46 46.4 33.7L288 439.6l130.7 68.7c23.2 12.2 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z"
          />
        </svg>
      </g>
    </svg>

    <div class="progress-ring__text-wrapper">
      <p :class="['progress-ring__text', progressRingTextModifier]">
        <span class="progress-ring__number">{{ numberDisplay(progress) }}</span>
        <span class="progress-ring__label">{{ label }}</span>
      </p>
    </div>
  </div>
</template>

<script>
  import numberDisplay from '@/helpers/number-display';

  export default {
    name: 'ProgressRing',

    props: {
      checkpoints: {
        type: Array,
        default() {
          return [];
        }
      },

      size: {
        type: String,
        default: ''
      },

      showProgressRingIndicator: {
        type: Boolean,
        default: false
      },

      label: {
        type: String,
        default: null
      },

      progress: {
        type: Number,
        default: 0
      },

      steps: {
        type: Number,
        default: 100
      }
    },

    data() {
      return {
        canvasSizeX: 100,
        canvasSizeY: 100,
        checkpointIconSize: 14,
        checkpointRadius: 12,
        graphRadius: 46,
        strokeWidth: 8
      };
    },

    computed: {
      calculatedCheckpoints() {
        /*
          NOTE: Math formula was inspired by this answer:
          https://stackoverflow.com/questions/13383112/python-arrange-images-on-canvas-in-a-circle/13384396#13384396
        */

        const result = [];
        const { steps } = this;
        const progress = Math.min(steps, this.progress);
        const radius = this.graphRadius;
        const centerX = this.canvasSizeX / 2;
        const centerY = this.canvasSizeY / 2;
        const theta = (2 * Math.PI) / steps;

        this.checkpoints.forEach((checkpoint) => {
          const angle = checkpoint.value * theta;
          const x = centerX + (radius * Math.cos(angle));
          const y = centerY + (radius * Math.sin(angle));
          const xRotated = y;
          const yRotated = (x * -1) + this.canvasSizeY;
          const finalX = Math.round(xRotated * 1000) / 1000;
          const finalY = Math.round(yRotated * 1000) / 1000;
          const isActivated = checkpoint.value <= progress;

          result.push({
            content: checkpoint.content,
            isActivated,
            value: checkpoint.value,
            x: finalX,
            y: finalY
          });
        });

        return result;
      },

      progressRingTextModifier() {
        /** Note: 'small' size currently only refers to the hamburger menu points display */
        if (this.size === 'small') {
          return this.showProgressRingIndicator
            ? 'progress-ring__text--xs'
            : 'progress-ring__text--sm';
        }

        return this.showProgressRingIndicator
          ? ''
          : 'progress-ring__text--lg';
      }
    },

    watch: {
      progress: 'renderProgress',
      steps: 'renderProgress'
    },

    mounted() {
      if (this.showProgressRingIndicator) this.renderProgress();
    },

    methods: {
      numberDisplay,

      renderProgress() {
        const { circle } = this.$refs;
        const radius = this.graphRadius;
        const circumference = radius * 2 * Math.PI;
        const { steps } = this;
        const progress = Math.min(steps, this.progress);

        circle.style.strokeDasharray = `${circumference} ${circumference}`;
        circle.style.strokeDashoffset = circumference;

        setTimeout(() => {
          // NOTE: This slight delay allows the CSS transistion animation to kick in properly
          this.$el.classList.add('progress-ring--animate');

          let offset;

          if (progress === 0) {
            offset = circumference;
          }
          else if (progress === steps) {
            offset = 0;
          }
          else {
            offset = circumference - ((progress / steps) * circumference);
          }

          circle.style.strokeDashoffset = offset;
        }, 25);
      }
    }
  };
</script>
